"use client";

import { useLoginMutation, useLoginWithNCALayerMutation } from "@/api/auth/authPublicApi";
import { useRequestPasswordResetViaCodeMutation } from "@/api/assignment/assignmentApi";
import { isRecoveryUserNotFound } from "@/api/register/accountRecoveryApi";
import { EyeIcon, EyeOffIcon, ShieldCheckIcon, OFFlineIcon } from "@/components/icons";
import NCALayer from "@/lib/NCALayer";
import { startAzureLogin } from "@/lib/azure";
import { isSystems247Brand } from "@/lib/branding";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useTranslations } from "next-intl";
import React, { useState, useEffect, useRef, useId } from "react";
import { toast } from "sonner";
import FullnamePasswordReset from "./FullnamePasswordReset";

const TECH_SUPPORT_EMAIL = "info@trustexam.ai";

interface LoginComponentProps {
  onToggle: () => void;
  onLoginSuccess?: () => void;
  externalSuccessMessage?: string | null;
  pddMode?: boolean;
}

function normalizeLoginIdentifier(value: string) {
  const trimmed = value.trim();
  return trimmed.includes("@") ? trimmed.toLowerCase() : trimmed;
}

function getPostLoginRedirect() {
  if (typeof window === "undefined") return "/assignments";
  const { pathname, search } = window.location;
  if (pathname.startsWith("/assignments/") && search) {
    return `${pathname}${search}`;
  }
  return "/assignments";
}

const LoginComponent: React.FC<LoginComponentProps> = ({
  onToggle,
  externalSuccessMessage,
  pddMode,
}) => {
  const t = useTranslations();
  const { copyToClipboard } = useCopyToClipboard();

  const primaryButtonClass = pddMode
    ? "cursor-pointer w-full py-3 rounded-lg font-medium text-white transition-all shadow-md h-[50px] md:h-auto disabled:opacity-60 disabled:cursor-not-allowed"
    : "";
  const pddGreenStyle = pddMode ? { backgroundColor: "#5AA06F" } : undefined;

  const [loading, setLoading] = useState(false);
  const [login] = useLoginMutation();

  const [isForgotView, setIsForgotView] = useState(false);
  const [remindLogin, setRemindLogin] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState(false);
  /* Аккаунт без телефона и почты: код не уходит, личность подтверждается Ф.И.О. */
  const [fullnameReset, setFullnameReset] = useState(false);
  const [requestPasswordResetViaCode, { isLoading: isForgotLoading }] =
    useRequestPasswordResetViaCodeMutation();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const usernameId = useId();
  const passwordId = useId();
  const loginErrorId = useId();
  const remindLoginId = useId();
  const forgotErrorId = useId();

  // SSO tab is only available for the systems247 brand
  const ssoEnabled = isSystems247Brand();

  // Tab state: "password" | "eds" | "app"
  const [activeTab, setActiveTab] = useState<"password" | "eds" | "sso">(
    pddMode ? "eds" : "password"
  );

  // NCALayer state
  const [ncaLayerConnected, setNcaLayerConnected] = useState(false);
  const [ncaLayerProcessing, setNcaLayerProcessing] = useState(false);
  const ncaLayerSocketRef = useRef<NCALayer | null>(null);
  const ncaLayerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [loginWithNCALayer] = useLoginWithNCALayerMutation();

  const connectNCALayer = () => {
    ncaLayerSocketRef.current = new NCALayer(
      () => {
        setNcaLayerConnected(true);
        setNcaLayerProcessing(false);
      },
      () => {
        setNcaLayerConnected(false);
        setNcaLayerProcessing(false);
      }
    );
  };

  useEffect(() => {
    if (activeTab !== "eds") return;

    if (!ncaLayerConnected) {
      connectNCALayer();
      ncaLayerIntervalRef.current = setInterval(connectNCALayer, 1000);
    } else {
      if (ncaLayerIntervalRef.current) {
        clearInterval(ncaLayerIntervalRef.current);
        ncaLayerIntervalRef.current = null;
      }
    }

    return () => {
      if (ncaLayerIntervalRef.current) {
        clearInterval(ncaLayerIntervalRef.current);
        ncaLayerIntervalRef.current = null;
      }
    };
  }, [activeTab, ncaLayerConnected]);

  const handleNCALayerSign = async () => {
    if (!ncaLayerSocketRef.current) return;
    setNcaLayerProcessing(true);

    try {
      const response = await ncaLayerSocketRef.current.signAuth("<data>test</data>");
      const signature = btoa(unescape(encodeURIComponent(response)));
      await loginWithNCALayer({ signature }).unwrap();
      toast.success(t("label-redirecting"));
      window.location.href = getPostLoginRedirect();
    } catch (error: any) {
      const status = error?.status;
      if (status === 400 || status === 401) {
        toast.error(t("error-invalid-credentials"));
      } else if (error?.code) {
        // NCALayer cancelled by user or error
        toast.error(t("error-server"));
      } else {
        toast.error(t("error-server"));
      }
    } finally {
      setNcaLayerProcessing(false);
    }
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setLoginError(null);
    const normalizedUsername = normalizeLoginIdentifier(username);
    if (normalizedUsername !== username) {
      setUsername(normalizedUsername);
    }

    try {
      await login({ username: normalizedUsername, password }).unwrap();
      toast.success(t("label-redirecting"));
      window.location.href = getPostLoginRedirect();
    } catch (error: any) {
      const status = error?.status;
      const message = (status === 400 || status === 401)
        ? t("error-invalid-credentials")
        : t("error-server");
      setLoginError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setForgotError(null);
    const normalizedLogin = normalizeLoginIdentifier(remindLogin);
    if (normalizedLogin !== remindLogin) {
      setRemindLogin(normalizedLogin);
    }

    try {
      const { verification_type } = await requestPasswordResetViaCode({
        username: normalizedLogin,
      }).unwrap();

      if (verification_type === "fullname") setFullnameReset(true);
      else setForgotSuccess(true);
    } catch (error: any) {
      console.error("Forgot password failed", error);
      const status = error?.status;
      /* «Не найден» бэк отдаёт 400 с кодом 0103, а не 404. */
      const message = isRecoveryUserNotFound(error)
        ? t("error-user-not-exists")
        : status === 429
          ? t("notification-request-reach-limit")
          : t("error-unknown-error");
      setForgotError(message);
      toast.error(message);
    }
  };

  const closeForgotView = () => {
    setActiveTab("password");
    setIsForgotView(false);
    setForgotSuccess(false);
    setFullnameReset(false);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <h2 className="text-[14px] font-bold text-gray-800 mb-2 text-center">
        {isForgotView ? t("label-restore-password") : t("label-authorization")}
      </h2>
      {!isForgotView && (
        <p className="text-gray-500 text-sm text-center mb-6">
          {t("label-auth-subtitle")}
        </p>
      )}

      {!isForgotView && (
        <>
          {/* Tab Navigation - Switch Style */}
          <div className="bg-gray-200 rounded-lg p-1 mb-6 flex h-[58px] md:h-auto">
            <button
              onClick={() => setActiveTab("password")}
              className={`cursor-pointer flex-1 h-full md:h-[36px] px-4 text-sm font-medium transition-all rounded-md ${activeTab === "password"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
                }`}
            >
              {t("btn-login-via-password")}
            </button>
            <button
              onClick={() => setActiveTab("eds")}
              className={`cursor-pointer flex-1 h-full md:h-[36px] px-4 text-sm font-medium transition-all rounded-md ${activeTab === "eds"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
                }`}
            >
              {t("btn-login-via-nca-layer")}
            </button>
            {ssoEnabled && (
              <button
                onClick={() => setActiveTab("sso")}
                className={`cursor-pointer flex-1 h-full md:h-[36px] px-4 text-sm font-medium transition-all rounded-md ${activeTab === "sso"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {t("btn-login-via-sso")}
              </button>
            )}
          </div>
        </>
      )}

      {activeTab === "password" && (
        <div>
          {isForgotView ? (
            <div>


              {fullnameReset ? (
                <FullnamePasswordReset
                  username={remindLogin}
                  onBack={closeForgotView}
                  onLoggedIn={() => {
                    toast.success(t("ui-forgot-password-success"));
                    window.location.href = getPostLoginRedirect();
                  }}
                />
              ) : forgotSuccess ? (
                <>
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {t("label-success-remind-description")}
                  </div>

                  <button
                    type="button"
                    onClick={closeForgotView}
                    className="cursor-pointer w-full py-3 rounded-lg font-medium text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all shadow-md"
                  >
                    {t("btn-entrace")}
                  </button>
                </>
              ) : (
                <form className="space-y-4" onSubmit={handleForgotPassword}>
                  {forgotError && (
                    <p
                      id={forgotErrorId}
                      role="alert"
                      className="text-sm text-red-600"
                    >
                      {forgotError}
                    </p>
                  )}

                  <label htmlFor={remindLoginId} className="sr-only">
                    {t("label-remind-login")}
                  </label>
                  <input
                    id={remindLoginId}
                    type="text"
                    autoComplete="username"
                    placeholder={t("label-remind-login")}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={remindLogin}
                    onChange={(e) => {
                      setRemindLogin(e.target.value);
                      if (forgotError) setForgotError(null);
                    }}
                    disabled={isForgotLoading}
                    required
                    aria-describedby={forgotError ? forgotErrorId : undefined}
                    aria-invalid={!!forgotError}
                  />

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={isForgotLoading}
                      className="cursor-pointer flex-1 py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md"
                    >
                      {isForgotLoading ? t("label-loading") : t("btn-send")}
                    </button>

                    <button
                      type="button"
                      onClick={closeForgotView}
                      className="cursor-pointer flex-1 py-3 rounded-lg font-medium text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all shadow-md"
                      disabled={isForgotLoading}
                    >
                      {t("btn-entrace")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <div>
              {externalSuccessMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  {externalSuccessMessage}
                </div>
              )}



              <form className="space-y-4" onSubmit={handleLogin}>
                {loginError && (
                  <p
                    id={loginErrorId}
                    role="alert"
                    className="text-sm text-red-600"
                  >
                    {loginError}
                  </p>
                )}

                <label htmlFor={usernameId} className="sr-only">
                  {t("label-login")}
                </label>
                <input
                  id={usernameId}
                  type="text"
                  autoComplete="username"
                  placeholder={t("label-login")}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    if (loginError) setLoginError(null);
                  }}
                  disabled={loading}
                  required
                  aria-describedby={loginError ? loginErrorId : undefined}
                  aria-invalid={!!loginError}
                />

                <label htmlFor={passwordId} className="sr-only">
                  {t("label-password")}
                </label>
                <div className="relative">
                  <input
                    id={passwordId}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder={t("label-password")}
                    className="w-full p-3 border border-gray-300 rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (loginError) setLoginError(null);
                    }}
                    disabled={loading}
                    required
                    aria-describedby={loginError ? loginErrorId : undefined}
                    aria-invalid={!!loginError}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
                  </button>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <a
                    href="#"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("password");
                      setIsForgotView(true);
                      setForgotSuccess(false);
                      setRemindLogin(username);
                    }}
                  >
                    {t("btn-forget-password")}
                  </a>
                  <button
                    type="button"
                    onClick={onToggle}
                    className="text-blue-600 hover:underline cursor-pointer"
                  >
                    {t("btn-register")}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={
                    pddMode
                      ? primaryButtonClass
                      : "cursor-pointer w-full py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md h-[50px] md:h-auto"
                  }
                  style={pddGreenStyle}
                >
                  {loading ? t("label-loading") : t("btn-entrace")}
                </button>

                <div className="text-sm text-center">
                  {t("label-technical-support")}:{" "}
                  <button
                    type="button"
                    onClick={async () => {
                      await copyToClipboard(TECH_SUPPORT_EMAIL);
                      toast.success(t("ui-copied"));
                    }}
                    className="cursor-pointer text-blue-600 underline"
                  >
                    {TECH_SUPPORT_EMAIL}
                  </button>
                </div>

                <p className="text-xs text-gray-500 text-center mt-4">
                  {t.rich("label-register-policy-agreement", {
                    terms: (chunks) => (
                      <a href="#" className="text-blue-600 hover:underline">
                        {chunks}
                      </a>
                    ),
                    privacy: (chunks) => (
                      <a href="#" className="text-blue-600 hover:underline">
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              </form>
            </div>
          )}
        </div>
      )}

      {/* EDS Tab Content */}
      {activeTab === "eds" && (
        <div>
          {/* Connection status panel */}
          <div
            className={`border p-4 rounded-lg mb-4 flex items-center space-x-3 ${
              ncaLayerConnected
                ? "bg-blue-50 border-blue-300"
                : "bg-red-50 border-red-300"
            }`}
          >
            {ncaLayerConnected ? (
              <ShieldCheckIcon size={20} className="text-blue-600 shrink-0" />
            ) : (
              <OFFlineIcon size={20} className="text-red-600 shrink-0" />
            )}
            <p
              className={`text-sm ${
                ncaLayerConnected ? "text-blue-700" : "text-red-700"
              }`}
            >
              {ncaLayerConnected
                ? t("label-nca-layer-ready")
                : t("label-nca-layer-disconnected")}
            </p>
          </div>

          {ncaLayerConnected ? (
            <button
              onClick={handleNCALayerSign}
              disabled={ncaLayerProcessing}
              className={
                pddMode
                  ? `${primaryButtonClass} mb-4`
                  : "cursor-pointer w-full py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md mb-4"
              }
              style={pddGreenStyle}
            >
              {ncaLayerProcessing ? t("label-loading") : t("btn-select-certificate")}
            </button>
          ) : (
            <button
              onClick={connectNCALayer}
              disabled={ncaLayerProcessing}
              className={
                pddMode
                  ? `${primaryButtonClass} mb-4`
                  : "cursor-pointer w-full py-3 rounded-lg font-medium text-white bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all shadow-md mb-4"
              }
              style={pddGreenStyle}
            >
              {ncaLayerProcessing ? t("label-loading") : t("btn-nca-layer-connect")}
            </button>
          )}

          <p className="text-xs text-gray-500 text-center mt-4">
            {t.rich("label-register-policy-agreement", {
              terms: (chunks) => (
                <a href="#" className="text-blue-600 hover:underline">
                  {chunks}
                </a>
              ),
              privacy: (chunks) => (
                <a href="#" className="text-blue-600 hover:underline">
                  {chunks}
                </a>
              ),
            })}
          </p>
        </div>
      )}

      {/* SSO Tab Content — Microsoft (Azure AD / Entra ID) */}
      {activeTab === "sso" && (
        <div>
          <p className="text-sm text-gray-500 text-center mb-4">
            {t("label-sso-description")}
          </p>

          <button
            type="button"
            onClick={() => {
              try {
                startAzureLogin();
              } catch {
                toast.error(t("error-server"));
              }
            }}
            className="cursor-pointer w-full flex items-center justify-center gap-3 py-3 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-all shadow-sm h-[50px] md:h-auto"
          >
            <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true" className="shrink-0">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            {t("btn-login-with-microsoft")}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            {t.rich("label-register-policy-agreement", {
              terms: (chunks) => (
                <a href="#" className="text-blue-600 hover:underline">
                  {chunks}
                </a>
              ),
              privacy: (chunks) => (
                <a href="#" className="text-blue-600 hover:underline">
                  {chunks}
                </a>
              ),
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default LoginComponent;
